import { useEffect, useMemo, useState } from "react";

import { CheckIcon, MinusIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { DateTime } from "luxon";

import LoadingWheel from "../../components/shared/LoadingWheel";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import useDailyChecks from "../../data/useDailyChecks";
import useWilmaUsers from "../../data/useWilmaUsers";
import { IDailyCheck, IDailyCheckDefinition, IDailyCheckItem, IDailyCheckStep } from "../../models/DailyChecks";
import classNames from "../../utils/classNames";
import formatDateTime from "../../utils/formatDateTime";

// completedBy holds an auth user id, so it needs resolving to a name before it goes on screen.
type ResolveUser = (userId?: string | null) => string | undefined;

function StepRow({ step, item, resolveUser }: { step: IDailyCheckStep, item?: IDailyCheckItem, resolveUser: ResolveUser }) {
  const isAnswered = item !== undefined && item.completed !== null;
  const isPassed = isAnswered && item.passed === true;
  const isFailed = isAnswered && !isPassed;

  const answeredBy = isAnswered ? resolveUser(item.completedBy) : undefined;
  const answeredAt = isAnswered && item.completed
    ? formatDateTime(item.completed, DateTime.TIME_24_SIMPLE)
    : undefined;

  return (
    <div
      title={answeredBy ? `${answeredBy}${answeredAt ? ` at ${answeredAt}` : ""}` : undefined}
      className={classNames(
        isFailed ? "bg-red-50" : "",
        "flex items-center justify-between px-4 py-2 text-sm"
      )}
    >
      <span className={classNames(isFailed ? "text-red-700" : "text-gray-900", "flex items-center gap-x-2")}>
        {step.name}
        {isRetired(step) && <span className="text-xs text-gray-400">retired</span>}
      </span>
      {isPassed ? (
        <CheckIcon className="h-5 w-5 text-green-600" />
      ) : isFailed ? (
        <XMarkIcon className="h-5 w-5 text-red-600" />
      ) : (
        <MinusIcon className="h-5 w-5 text-gray-400" />
      )}
    </div>
  );
}

const isRetired = (x: { isActive: boolean, deleted: string | null }) => !x.isActive || x.deleted !== null;

function DailyCheckCard({ definition, check, resolveUser }: { definition: IDailyCheckDefinition, check?: IDailyCheck, resolveUser: ResolveUser }) {
  // A step retired since is still part of that day's record, so show any step with an answer plus
  // the steps live today. Counting only today's active steps would rewrite the past - a step added
  // later would make a finished day read as incomplete, and a retired one would hide its failure.
  // Steps keep their recorded order so the day reads in the sequence it was worked through. A retired
  // step's order can coincide with a live one's - reordering only renumbers the live steps - so name
  // breaks the tie rather than leaving it to chance.
  const steps = [...definition.steps]
    .filter((step) => !isRetired(step) || check?.items.some((item) => item.dailyCheckStepId === step.id))
    .sort((a, b) => a.order - b.order || a.name.localeCompare(b.name));

  const completedByName = resolveUser(check?.completedBy);

  const completedItems = steps.filter((step) =>
    check?.items.some((item) => item.dailyCheckStepId === step.id && item.completed !== null)
  ).length;

  const status = check?.completed
    ? {
      label: `Completed ${formatDateTime(check.completed, DateTime.TIME_24_SIMPLE)}`,
      classes: "bg-green-100 text-green-800",
    }
    : check && completedItems > 0
      ? {
        label: `In progress ${completedItems}/${steps.length}`,
        classes: "bg-amber-100 text-amber-800",
      }
      : {
        label: "Not started",
        classes: "bg-gray-100 text-gray-600",
      };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
        <div>
          <div className="flex items-center gap-x-2">
            <h2 className="text-base font-medium text-gray-900">{definition.name}</h2>
            {isRetired(definition) && (
              <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                Retired
              </span>
            )}
          </div>
          {check?.completed && completedByName && (
            <p className="text-xs text-gray-500">By {completedByName}</p>
          )}
        </div>
        <span className={classNames(status.classes, "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium")}>
          {status.label}
        </span>
      </div>
      <div className="divide-y divide-gray-100">
        {steps.map((step) => (
          <StepRow
            key={step.id}
            step={step}
            item={check?.items.find((item) => item.dailyCheckStepId === step.id)}
            resolveUser={resolveUser}
          />
        ))}
        {steps.length === 0 && (
          <p className="px-4 py-2 text-sm text-gray-500">No active steps.</p>
        )}
      </div>
      {check?.notes && (
        <div className="border-t border-gray-200 px-4 py-3">
          <p className="text-xs font-medium text-gray-500">Notes</p>
          <p className="text-sm text-gray-900 whitespace-pre-line">{check.notes}</p>
        </div>
      )}
    </div>
  );
}

export default function DailyChecksResults() {
  const { setInfo } = usePageNameContext();
  const [date, setDate] = useState<string>(DateTime.now().setZone("Europe/London").toFormat("yyyy-MM-dd"));
  // Retired definitions are needed to label the results of days they were still running on.
  const { definitions, checksForDate } = useDailyChecks(date, true);
  const { wilmaUsers } = useWilmaUsers();

  const userNames = useMemo(() => {
    const names = new Map<string, string>();
    wilmaUsers.data?.data?.forEach((user) => names.set(user.id.toLowerCase(), user.fullname));
    return names;
  }, [wilmaUsers.data]);

  // Nothing rather than a raw id while the users are still loading, or if one can't be matched.
  const resolveUser: ResolveUser = (userId) => {
    if (!userId || wilmaUsers.isLoading) return undefined;
    return userNames.get(userId.toLowerCase()) ?? "Unknown user";
  };

  useEffect(() => {
    setInfo({
      name: "Daily Checks",
      desc: "View daily check results for a given day",
    });
  }, [setInfo]);

  const checks = checksForDate.data?.data ?? [];

  // Everything live today, plus anything with a result recorded on this date even if it has since
  // been deactivated or deleted. Filtering on isActive alone would erase a retired check from every
  // day it ever ran on, which is the record the day's sign-off was made against.
  // Reordering renumbers only the live definitions, so a retired one can share a live one's order.
  // Grouping retired last keeps them out of the running order and makes the tie moot; name settles
  // anything still equal so the layout can't shuffle between loads.
  const visibleDefinitions = definitions.data
    ? [...definitions.data.data]
      .filter((x) => !isRetired(x) || checks.some((check) => check.dailyCheckDefinitionId === x.id))
      .sort((a, b) => {
        if (isRetired(a) !== isRetired(b)) return isRetired(a) ? 1 : -1;
        return a.order - b.order || a.name.localeCompare(b.name);
      })
    : undefined;

  return definitions.isLoading || checksForDate.isLoading || !visibleDefinitions ? (
    <div className="mt-8 min-h-[50vh] my-4 p-4 bg-white rounded-lg shadow">
      <LoadingWheel />
    </div>
  ) : (
    <>
      <div className="mt-4 flex items-center gap-x-4">
        <label className="text-sm font-medium text-gray-500">Date</label>
        <input
          type="date"
          value={date}
          onChange={(e) => e.target.value && setDate(e.target.value)}
          className="text-sm text-gray-900 border-1 border-gray-300 rounded-md focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
        />
      </div>

      {visibleDefinitions.length === 0 ? (
        <p className="mt-8 text-sm text-gray-500">No active daily checks have been set up yet.</p>
      ) : (
        <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3">
          {visibleDefinitions.map((definition) => (
            <DailyCheckCard
              key={definition.id}
              definition={definition}
              check={checks.find((x) => x.dailyCheckDefinitionId === definition.id)}
              resolveUser={resolveUser}
            />
          ))}
        </div>
      )}
    </>
  );
}
